import { batch, createMemo, createSignal, type Accessor } from 'solid-js';

import { useAppContext } from '../AppContextProvider';
import { RightPanelTabName } from '../constants/panel-constants';
import type { FxExtractionWorkflow } from './fx-extraction-workflow.types';

export interface FxExtractionWorkflowApi {
  readonly selectedExtractionWorkflow: Accessor<
    FxExtractionWorkflow | undefined
  >;
  readonly isNewExtractionDialogOpen: Accessor<boolean>;
  openNewExtractionDialog(): void;
  closeNewExtractionDialog(): void;
  requestChangeExtractionWorkflow(): void;
  confirmExtractionWorkflowSelection(workflow: FxExtractionWorkflow): void;
  resetRepositoryScope(): void;
}

export function useFxExtractionWorkflow(): FxExtractionWorkflowApi {
  const appContext = useAppContext();
  const [extractionWorkflow, setExtractionWorkflow] = createSignal<
    FxExtractionWorkflow | undefined
  >(undefined);
  const [isNewExtractionDialogOpen, setIsNewExtractionDialogOpen] =
    createSignal(false);

  const selectedExtractionWorkflow = createMemo(() => {
    const isWorkflowSessionOpen =
      appContext.selectedRightPanelTab() ===
        RightPanelTabName.ExtractionWorkflow || isNewExtractionDialogOpen();
    return isWorkflowSessionOpen ? extractionWorkflow() : undefined;
  });

  const openNewExtractionDialog = (): void => {
    batch(() => {
      if (
        appContext.selectedRightPanelTab() !==
        RightPanelTabName.ExtractionWorkflow
      ) {
        setExtractionWorkflow(undefined);
      }
      setIsNewExtractionDialogOpen(true);
    });
  };

  const closeNewExtractionDialog = (): void => {
    setIsNewExtractionDialogOpen(false);
  };

  const requestChangeExtractionWorkflow = (): void => {
    batch(() => {
      setIsNewExtractionDialogOpen(true);
      appContext.setSelectedRightPanelTab(undefined);
    });
  };

  const confirmExtractionWorkflowSelection = (
    workflow: FxExtractionWorkflow
  ): void => {
    batch(() => {
      setExtractionWorkflow(workflow);
      appContext.setSelectedRightPanelTab(RightPanelTabName.ExtractionWorkflow);
      setIsNewExtractionDialogOpen(false);
    });
  };

  return {
    selectedExtractionWorkflow,
    isNewExtractionDialogOpen,
    openNewExtractionDialog,
    closeNewExtractionDialog,
    requestChangeExtractionWorkflow,
    confirmExtractionWorkflowSelection,
    resetRepositoryScope: (): void => {
      batch(() => {
        setExtractionWorkflow(undefined);
        setIsNewExtractionDialogOpen(false);
      });
    },
  };
}
